/**
 * Structured pricing helpers — convert AI-generated pricing into a
 * deterministic array of line items, compute totals/VAT server-side,
 * and render the canonical Markdown table from those items.
 *
 * The proposal_drafts table now stores both:
 *   - pricing_table  TEXT   — the human-readable Markdown table (for
 *                              backward compat with the existing editor /
 *                              old exports / DOCX template merge fields).
 *   - pricing_items  JSONB  — the structured `PricingDocument` shape
 *                              below, the new source of truth for export
 *                              and totals.
 */

export interface PricingLineItem {
  description: string;
  quantity: number;
  unit: string;        // e.g. "ساعة", "وحدة", "hour", "month"
  unitPrice: number;   // SAR per unit
  total: number;       // SAR, computed = quantity * unitPrice
  source?: string;     // free text or URL — where the price came from
  notes?: string;
}

export interface PricingDocument {
  currency: "SAR";
  items: PricingLineItem[];
  subtotal: number;
  vatRate: number;     // 0.15 for Saudi 15%
  vatAmount: number;
  total: number;
  notes?: string;
}

const SAUDI_VAT = 0.15;

function n(x: any): number {
  if (typeof x === "number" && Number.isFinite(x)) return x;
  if (typeof x !== "string") return 0;
  // strip currency, separators, Arabic-Indic digits → ASCII
  const ascii = x
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[,\u066B\u066C\s]/g, "")
    .replace(/[^\d.\-]/g, "");
  const v = parseFloat(ascii);
  return Number.isFinite(v) ? v : 0;
}

/**
 * Compute subtotal/VAT/total deterministically server-side. Mutates a
 * shallow copy of `doc` so the caller can trust the numbers.
 */
export function recomputeTotals(doc: PricingDocument): PricingDocument {
  const items = doc.items.map((it) => {
    const quantity = n(it.quantity);
    const unitPrice = n(it.unitPrice);
    const total = Math.round(quantity * unitPrice * 100) / 100;
    return { ...it, quantity, unitPrice, total };
  });
  const subtotal = Math.round(items.reduce((s, it) => s + it.total, 0) * 100) / 100;
  const vatRate = typeof doc.vatRate === "number" && doc.vatRate >= 0 ? doc.vatRate : SAUDI_VAT;
  const vatAmount = Math.round(subtotal * vatRate * 100) / 100;
  const total = Math.round((subtotal + vatAmount) * 100) / 100;
  return { currency: "SAR", items, subtotal, vatRate, vatAmount, total, notes: doc.notes };
}

/** Parse a markdown pipe-table into structured line items (best-effort). */
export function parseMarkdownPricingTable(markdown: string | null | undefined): PricingDocument | null {
  const md = (markdown ?? "").trim();
  if (!md) return null;
  const lines = md.split(/\r?\n/).map((l) => l.trim()).filter((l) => l.startsWith("|"));
  if (lines.length < 2) return null;

  // Drop separator rows like |---|---|
  const rows = lines.filter((l) => !/^\|\s*:?-{2,}/.test(l.replace(/\s/g, "")));
  if (rows.length < 2) return null;

  const header = rows[0]
    .split("|").slice(1, -1).map((c) => c.trim().toLowerCase());
  const body = rows.slice(1);

  const findIdx = (...patterns: RegExp[]): number => {
    for (let i = 0; i < header.length; i++) {
      if (patterns.some((p) => p.test(header[i]))) return i;
    }
    return -1;
  };

  const iDesc = findIdx(/(description|item|بند|الوصف|البند)/);
  const iQty = findIdx(/(qty|quantity|الكمية|عدد)/);
  const iUnit = findIdx(/(^unit$|الوحدة)/);
  const iPrice = findIdx(/(unit\s*price|سعر\s*الوحدة|السعر)/);
  const iTotal = findIdx(/(total|إجمالي|الإجمالي|مجموع)/);

  if (iDesc < 0 && iPrice < 0 && iTotal < 0) return null;

  const items: PricingLineItem[] = [];
  for (const row of body) {
    const cells = row.split("|").slice(1, -1).map((c) => c.trim());
    if (cells.every((c) => !c)) continue;
    const desc = (iDesc >= 0 ? cells[iDesc] : cells[0] ?? "").trim();
    // Skip subtotal / VAT / grand-total rows — they're recomputed
    if (/(^|[\s|])(subtotal|vat|grand total|إجمالي|الإجمالي|قبل الضريبة|ضريبة|بعد الضريبة)/i.test(desc) && !cells.slice(1).some((c) => /\d/.test(c) === false)) {
      // Only skip if it looks like a totals row (description is one of the keywords AND no quantity)
      const qtyCell = iQty >= 0 ? cells[iQty] : "";
      if (!qtyCell || n(qtyCell) === 0) continue;
    }
    if (!desc) continue;
    const quantity = iQty >= 0 ? n(cells[iQty]) : 1;
    const unit = iUnit >= 0 ? cells[iUnit] : "";
    const unitPrice = iPrice >= 0 ? n(cells[iPrice]) : (iTotal >= 0 ? n(cells[iTotal]) / Math.max(quantity, 1) : 0);
    const total = iTotal >= 0 ? n(cells[iTotal]) : quantity * unitPrice;
    if (quantity === 0 && unitPrice === 0 && total === 0) continue;
    items.push({ description: desc, quantity, unit, unitPrice, total });
  }

  if (items.length === 0) return null;
  return recomputeTotals({
    currency: "SAR", items,
    subtotal: 0, vatRate: SAUDI_VAT, vatAmount: 0, total: 0,
  });
}

/** Render a PricingDocument back to a canonical Markdown table. */
export function renderPricingTableMarkdown(
  doc: PricingDocument,
  language: "ar" | "en",
): string {
  const isAr = language === "ar";
  const headers = isAr
    ? ["#", "البند", "الكمية", "الوحدة", "سعر الوحدة (ر.س)", "الإجمالي (ر.س)"]
    : ["#", "Description", "Qty", "Unit", "Unit Price (SAR)", "Total (SAR)"];
  const fmt = (v: number) =>
    v.toLocaleString(isAr ? "ar-SA" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const lines: string[] = [];
  lines.push(`| ${headers.join(" | ")} |`);
  lines.push(`| ${headers.map(() => "---").join(" | ")} |`);
  doc.items.forEach((it, i) => {
    lines.push(`| ${i + 1} | ${it.description} | ${fmt(it.quantity)} | ${it.unit || ""} | ${fmt(it.unitPrice)} | ${fmt(it.total)} |`);
  });
  // Totals rows
  const subLabel = isAr ? "الإجمالي قبل الضريبة" : "Subtotal";
  const vatLabel = isAr ? `ضريبة القيمة المضافة (${Math.round(doc.vatRate * 100)}٪)` : `VAT (${Math.round(doc.vatRate * 100)}%)`;
  const totLabel = isAr ? "الإجمالي مع الضريبة" : "Grand Total (incl. VAT)";
  lines.push(`| | **${subLabel}** | | | | **${fmt(doc.subtotal)}** |`);
  lines.push(`| | **${vatLabel}** | | | | **${fmt(doc.vatAmount)}** |`);
  lines.push(`| | **${totLabel}** | | | | **${fmt(doc.total)}** |`);
  return lines.join("\n");
}

/**
 * Format the grand total as a short string for the `total_amount` column.
 */
export function formatTotalAmount(doc: PricingDocument, language: "ar" | "en"): string {
  const isAr = language === "ar";
  const v = doc.total.toLocaleString(isAr ? "ar-SA" : "en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  return isAr ? `${v} ر.س (شامل ضريبة القيمة المضافة)` : `SAR ${v} (incl. VAT)`;
}
